import { useEffect, useMemo } from "react";
import { genericEntityConstants, GenericEntityConstantsEntities } from "../../api/GenericConstants";
import Select from "react-select";
import { GenericEntitySelectWidget } from "./common/GenericEntitySelectWdiget";
import { IEntityMinimalModel } from "../../api/GenericTypes";
import { useForm } from "react-hook-form";
import {
  DefaultGenericEntityComponents,
  GenericEntity,
  GenericEntityComponentKeys,
  GenericEntityComponentKeysType,
  IndexComponentKeys,
} from "./GenericComponents";
import { useEntityDetail } from "../../api/BaseEntityApi";
import { toUppercase } from "../../common/helperfunctions/stringFunctions";
import { useHistory } from "react-router-dom";

export const EntityTypeIdSelect = ({
  value: entityTypeId,
  onChange,
  entityTypeIdOverride,
}: {
  value: GenericEntityConstantsEntities | undefined;
  onChange: (entityTypeId: GenericEntityConstantsEntities) => void;
  entityTypeIdOverride?: GenericEntityConstantsEntities[];
}) => {
  const typeIds = useMemo(() => {
    return entityTypeIdOverride ?? Object.keys(genericEntityConstants);
  }, [entityTypeIdOverride]);

  return (
    <Select
      options={typeIds.map((entityTypeId) => ({
        id: entityTypeId as GenericEntityConstantsEntities,
        value: entityTypeId as GenericEntityConstantsEntities,
        label: toUppercase(
          genericEntityConstants[entityTypeId as GenericEntityConstantsEntities]?.entityConstants.entityPlural
        ),
      }))}
      value={
        !!entityTypeId
          ? {
              id: entityTypeId,
              value: entityTypeId,
              label: toUppercase(
                genericEntityConstants[entityTypeId as GenericEntityConstantsEntities]?.entityConstants.entityPlural
              ),
            }
          : undefined
      }
      onChange={(selectedOption) => onChange(selectedOption?.value as GenericEntityConstantsEntities)}
      isClearable
      placeholder="Select entity type..."
    />
  );
};

export const EntityComponentSelect = ({
  value: entityComponentId,
  onChange,
}: {
  value: GenericEntityComponentKeysType | undefined;
  onChange: (entityComponentId: GenericEntityComponentKeysType) => void;
}) => {
  return (
    <Select
      options={GenericEntityComponentKeys.map((entityComponentId: any) => ({
        id: entityComponentId,
        value: entityComponentId,
        label: entityComponentId,
      }))}
      value={!!entityComponentId ? { value: entityComponentId, label: entityComponentId } : undefined}
      onChange={(selectedOption) => onChange(selectedOption?.value as GenericEntityComponentKeysType)}
      isClearable
      placeholder="Select component..."
    />
  );
};

const ImplementedEntityTypes: GenericEntityConstantsEntities[] = [
  "announcements",
  "attachments",
  "auditLogs",
  "customFields",
  "customTypes",
  "datasets",
  "inventoryItems",
  "methods",
  "notebookEntries",
  "notebookExperiments",
  "notebookTemplates",
  "notebooks",
  "persons",
  "projects",
  "samples",
  "roles",
];

const EntityTypeIdURLParam = "entityTypeId";
const EntityComponentIdURLParam = "entityComponentId";
export const EntityTypeComponentDemoWrapper = () => {
  const history = useHistory();
  const entityTypeId = useMemo(
    () => new URLSearchParams(history.location.search).get(EntityTypeIdURLParam) as GenericEntityConstantsEntities,
    [history.location.search]
  );
  const setEntityTypeId = (entityTypeId: GenericEntityConstantsEntities) => {
    const searchParams = new URLSearchParams(history.location.search);
    !!entityTypeId ? searchParams.set(EntityTypeIdURLParam, entityTypeId) : searchParams.delete(EntityTypeIdURLParam);
    history.replace({ search: searchParams.toString() });
  };
  const entityInfo = entityTypeId ? genericEntityConstants[entityTypeId] ?? undefined : undefined;

  const entityComponentId = useMemo(
    () => new URLSearchParams(history.location.search).get(EntityComponentIdURLParam) as GenericEntityComponentKeysType,
    [history.location.search]
  );
  const setEntityComponentId = (entityComponentId: GenericEntityComponentKeysType) => {
    const searchParams = new URLSearchParams(history.location.search);
    !!entityComponentId
      ? searchParams.set(EntityComponentIdURLParam, entityComponentId)
      : searchParams.delete(EntityComponentIdURLParam);
    history.replace({ search: searchParams.toString() });
  };

  const { control, watch, resetField } = useForm<IEntityMinimalModel>({});
  const fieldId = `${entityTypeId}-DemoSelect` as any;
  const entity: IEntityMinimalModel = watch(fieldId as any);
  const { data: fullEntity } = useEntityDetail<GenericEntity>(
    entityInfo ? entityInfo.entityConstants.resource : "announcements",
    entity?.id,
    undefined,
    { enabled: !!entityInfo && !!entity?.id }
  );
  const Component = entityComponentId ? DefaultGenericEntityComponents[entityComponentId] : null;
  useEffect(() => {
    resetField(fieldId as any);
  }, [entityTypeId, fieldId, resetField]);

  return (
    <div style={{ width: "100%", marginBottom: "25px" }}>
      <h5>Entity type:</h5>
      <EntityTypeIdSelect
        value={entityTypeId}
        onChange={setEntityTypeId}
        entityTypeIdOverride={ImplementedEntityTypes}
      />
      <>
        <hr />
        <h5>Entity component:</h5>
        <EntityComponentSelect value={entityComponentId} onChange={setEntityComponentId} />
      </>
      {!!entityTypeId && !!entityComponentId && !IndexComponentKeys.includes(entityComponentId) && (
        <>
          <hr />
          <GenericEntitySelectWidget entityTypeId={entityTypeId} componentProps={{ id: fieldId as any, control }} />
        </>
      )}
      {!!entityComponentId && !!entityTypeId && (IndexComponentKeys.includes(entityComponentId) || !!fullEntity) && (
        <>
          <hr />
          <h5>Example:</h5>
          <div style={{ height: "80vh", width: "100%", overflow: "auto", resize: "both", padding: "5px" }}>
            {Component && <Component entityTypeId={entityTypeId} entity={fullEntity} />}
          </div>
        </>
      )}
    </div>
  );
};
